import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '~components/generic/editor/Button/Button';
import { Chip } from '~components/generic/editor/Chip/Chip';
import { UserSearch } from '~components/generic/UserSearch/UserSearch';
import { CheckIcon, TeamAddIcon, UserAddIcon, WarningIcon } from '~components/icons';
import useLabels from '~hooks/useLabels';
import { useAddUsersToUsergroup, useUsergroup } from '~services/api/hooks/user-management.hooks';
import { AddUsersTable } from './AddUsersTable';
import styles from './AddUsersForm.module.css';

export type AddUsersFormProps = {
  usergroup: UsergroupShape;
};

type User = UserSearchResultItem & { selected?: boolean };

const fnFilterSelectedUsers = (user: User) => Boolean(user.selected);
const fnFilterNotAlreadyInUsergroupUsers = (usergroup: UsergroupShape) => (user: User) =>
  !(usergroup.users || []).includes(user.username);

export function AddUsersForm({ usergroup }: AddUsersFormProps) {
  const { placeholderAddMultipleUsers } = useLabels(['placeholderAddMultipleUsers']);
  const [users, setUsers] = useState<User[]>(undefined);
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState<boolean>(undefined);
  const hasChanges = useMemo(() => !!(users || []).find(fnFilterSelectedUsers), [users]);
  const isMounted = useRef(false);

  // Trigger: get latest usergroup data
  useUsergroup(usergroup.name);

  // Define: state update for notifing user about successful update
  const updateSuccess = useCallback((success: boolean) => {
    setIsUpdateSuccessful(success);
    setTimeout(() => {
      if (isMounted.current) {
        setIsUpdateSuccessful(undefined);
      }
    }, 5000);
  }, []);

  // Define: patch usergroup add users query
  const { mutate: patchUsergroupAddUser, isPending: isProcessing } = useAddUsersToUsergroup({
    onSuccess: () => {
      updateSuccess(true);
    },
    onError: () => {
      updateSuccess(false);
    },
  });

  // Handler: Add user click
  const handleOnAddUsersClick = useCallback(() => {
    const usersToAdd = (users || [])
      .filter(fnFilterSelectedUsers)
      .filter(fnFilterNotAlreadyInUsergroupUsers(usergroup));
    if (usersToAdd.length > 0) {
      patchUsergroupAddUser({ groupName: usergroup.name, userNames: usersToAdd.map(user => user.username) });
    }
  }, [patchUsergroupAddUser, usergroup, users]);

  // Trigger: register mounted / unmounted
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <FormattedMessage id="hlAddMultipleUsers" defaultMessage="hlAddMultipleUsers" />
        </div>
        <div className={styles.name}>
          <FormattedMessage id="labelUsergroupName" defaultMessage="labelUsergroupName" />: {usergroup.name}
        </div>
      </div>
      <div className={styles.search}>
        <UserSearch
          onSearchResult={setUsers}
          icon={<UserAddIcon />}
          placeholderText={placeholderAddMultipleUsers}
          disabled={isProcessing}
          autoFocus
        />
      </div>
      {users ? (
        users.length > 0 ? (
          <div className={styles.table}>
            <AddUsersTable data={users} disabled={isProcessing} onUpdateData={setUsers} />
          </div>
        ) : (
          <div className={styles.message}>
            <FormattedMessage id="msgAddMultipleUsersNothingFound" defaultMessage="msgAddMultipleUsersNothingFound" />
          </div>
        )
      ) : null}

      <div className={styles.footer}>
        {isUpdateSuccessful === true && (
          <div className={styles.footerAction}>
            <FormattedMessage id="msgAddMultipleUsersSaveSuccessful" defaultMessage="Saved!">
              {(msg: any) => <Chip label={msg} status="success" preIcon={<CheckIcon />} />}
            </FormattedMessage>
          </div>
        )}
        {isUpdateSuccessful === false && (
          <div className={styles.footerAction}>
            <FormattedMessage id="msgAddMultipleUsersSaveFailed" defaultMessage="Error!">
              {(msg: any) => <Chip label={msg} status="error" preIcon={<WarningIcon />} />}
            </FormattedMessage>
          </div>
        )}
        <div className={styles.footerAction}>
          <FormattedMessage id="btnSaveAddUsers" defaultMessage="Save">
            {(msg: any) => (
              <Button
                onClick={handleOnAddUsersClick}
                disabled={!hasChanges}
                processing={isProcessing}
                label={msg}
                icon={<TeamAddIcon />}
              />
            )}
          </FormattedMessage>
        </div>
      </div>
    </div>
  );
}
